/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable no-eval */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    NgZone,
    OnDestroy,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DomHandler } from 'primeng/dom';
import { ActionKeys } from '../../enums/keys.enum';
import { Debounce } from 'lodash-decorators';

export const SLIDER_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SliderComponent),
    multi: true
};

/**
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - use this to associate an external label with the slider. Use id of the label as this value
 * + ***ariaValueText*** - use this to set an accessible label to the slider
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    providers: [SLIDER_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SliderComponent implements OnDestroy, ControlValueAccessor {

    @Input() id: string;
    @Input() animate: boolean;
    @Input() disabled: boolean;
    @Input() min: number = 0;
    @Input() max: number = 100;
    @Input() orientation: string = 'horizontal';
    @Input() step: number;
    @Input() range: boolean;

    @Input() style: any;
    @Input() styleClass: string;

    @Input() ariaLabelledBy: string = '';
    @Input() ariaValueText: string;

    @Input() tabindex: number = 0;
    @Input() showValue: boolean = true;

    @Output() onChange: EventEmitter<any> = new EventEmitter();
    @Output() onSlideEnd: EventEmitter<any> = new EventEmitter();

    @ViewChild('sliderHandle') sliderHandle: ElementRef;
    @ViewChild('sliderHandleStart') sliderHandleStart: ElementRef;
    @ViewChild('sliderHandleEnd') sliderHandleEnd: ElementRef;

    public value: number;
    public values: number[];
    public handleValue: number;
    public handleValues: number[] = [];
    public dragging: boolean;
    public dragListener: any;
    public mouseupListener: any;
    public initX: number;
    public initY: number;
    public barWidth: number;
    public barHeight: number;
    public sliderHandleClick: boolean;
    public handleIndex: number = 0;
    public startHandleValue: any;
    public startx: number;
    public starty: number;

    public readonly actionKeysEnum = ActionKeys;

    constructor(
        public el: ElementRef,
        public renderer: Renderer2,
        private ngZone: NgZone,
        public cd: ChangeDetectorRef
    ) {
    }

    public onModelChange: Function = () => {};

    public onModelTouched: Function = () => {};

    onMouseDown(event, index?: number) {
        if (this.disabled) {
            return;
        }

        this.dragging = true;
        this.updateDomData();
        this.sliderHandleClick = true;
        if (this.range && this.handleValues && this.handleValues[0] === this.max) {
            this.handleIndex = 0;
        } else {
            this.handleIndex = index;
        }

        this.bindDragListeners();
        event.target.focus();
        event.preventDefault();

        if (this.animate) {
            DomHandler.removeClass(this.el.nativeElement.children[0], 'p-slider-animate');
        }
    }

    onTouchStart(event, index?: number) {
        if (this.disabled) {
            return;
        }

        const touchobj = event.changedTouches[0];
        this.startHandleValue = (this.range) ? this.handleValues[index] : this.handleValue;
        this.dragging = true;
        if (this.range && this.handleValues && this.handleValues[0] === this.max) {
            this.handleIndex = 0;
        } else {
            this.handleIndex = index;
        }

        if (this.orientation === 'horizontal') {
            this.startx = parseInt(touchobj.clientX, 10);
            this.barWidth = this.el.nativeElement.children[0].offsetWidth;
        } else {
            this.starty = parseInt(touchobj.clientY, 10);
            this.barHeight = this.el.nativeElement.children[0].offsetHeight;
        }

        if (this.animate) {
            DomHandler.removeClass(this.el.nativeElement.children[0], 'p-slider-animate');
        }

        event.preventDefault();
    }

    onTouchMove(event, index?: number) {
        if (this.disabled) {
            return;
        }

        const touchobj = event.changedTouches[0];
        let handleValue = 0;

        if (this.orientation === 'horizontal') {
            handleValue = Math.floor(((parseInt(touchobj.clientX, 10) - this.startx) * 100) / (this.barWidth)) + this.startHandleValue;
        } else {
            handleValue = Math.floor(((this.starty - parseInt(touchobj.clientY, 10)) * 100) / (this.barHeight))  + this.startHandleValue;
        }

        this.setValueFromHandle(event, handleValue);

        event.preventDefault();
    }

    onTouchEnd(event, index?: number) {
        if (this.disabled) {
            return;
        }

        this.dragging = false;

        if (this.range) {
            this.onSlideEnd.emit({originalEvent: event, values: this.values});
        } else {
            this.onSlideEnd.emit({originalEvent: event, value: this.value});
        }

        if (this.animate) {
            DomHandler.addClass(this.el.nativeElement.children[0], 'p-slider-animate');
        }

        event.preventDefault();
    }

    onBarClick(event) {
        if (this.disabled) {
            return;
        }

        if (!this.sliderHandleClick) {
            this.updateDomData();
            this.handleChange(event);
        }

        this.sliderHandleClick = false;
    }

    onHandleKeydown(event, handleIndex?: number) {
        if (this.disabled) {
            return;
        }
        if (event.key === this.actionKeysEnum.UP || event.key === this.actionKeysEnum.RIGHT) {
            this.spin(event, 1, handleIndex);
        } else if (event.key === this.actionKeysEnum.DOWN || event.key === this.actionKeysEnum.LEFT) {
            this.spin(event, -1, handleIndex);
        }
    }

    spin(event, dir: number, handleIndex?: number) {
        const step = (this.step || 1) * dir;

        if (this.range) {
            this.handleIndex = handleIndex;
            this.updateValue(this.values[this.handleIndex] + step);
            this.updateHandleValue();
        } else {
            this.updateValue(this.value + step);
            this.updateHandleValue();
        }

        event.preventDefault();
    }

    handleChange(event: Event, isMouseEvent: boolean = false) {
        const handleValue = this.calculateHandleValue(event);
        this.setValueFromHandle(event, handleValue, isMouseEvent);
    }

    bindDragListeners() {
        this.ngZone.runOutsideAngular(() => {
            const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

            if (!this.dragListener) {
                this.dragListener = this.renderer.listen(documentTarget, 'mousemove', (event) => {
                    if (this.dragging) {
                        this.ngZone.run(() => {
                            this.handleChange(event, true);
                        });
                    }
                });
            }

            if (!this.mouseupListener) {
                this.mouseupListener = this.renderer.listen(documentTarget, 'mouseup', (event) => {
                    if (this.dragging) {
                        this.dragging = false;
                        this.ngZone.run(() => {
                            if (this.range) {
                                this.updateParentValues();
                                this.onSlideEnd.emit({originalEvent: event, values: this.values});
                            } else {
                                this.updateParentValue();
                                this.onSlideEnd.emit({originalEvent: event, value: this.value});
                            }
                            if (this.animate) {
                                DomHandler.addClass(this.el.nativeElement.children[0], 'p-slider-animate');
                            }
                        });
                    }
                });
            }
        });
    }

    unbindDragListeners() {
        if (this.dragListener) {
            this.dragListener();
        }

        if (this.mouseupListener) {
            this.mouseupListener();
        }
    }

    setValueFromHandle(event: Event, handleValue: any, isMouseEvent: boolean = false) {
        this.sliderHandleClick = false;
        const newValue = this.getValueFromHandle(handleValue);

        if (this.range) {
            if (this.step) {
                this.handleStepChange(newValue, this.values[this.handleIndex], isMouseEvent);
            } else {
                this.handleValues[this.handleIndex] = handleValue;
                this.updateValue(newValue, event, isMouseEvent);
            }
        } else {
            if (this.step) {
                this.handleStepChange(newValue, this.value, isMouseEvent);
            } else {
                this.handleValue = handleValue;
                this.updateValue(newValue, event, isMouseEvent);
            }
        }

        this.cd.markForCheck();
    }

    handleStepChange(newValue: number, oldValue: number, isMouseEvent: boolean = false) {
        const diff = (newValue - oldValue);
        let val = oldValue;

        if (diff < 0) {
            val = oldValue + Math.ceil(newValue / this.step - oldValue / this.step) * this.step;
        } else if (diff > 0) {
            val = oldValue + Math.floor(newValue / this.step - oldValue / this.step) * this.step;
        }

        this.updateValue(val, null, isMouseEvent);
        this.updateHandleValue();
    }

    writeValue(value: any): void {
        if (this.range) {
            this.values = value || [0, 0];
        } else {
            this.value = value || 0;
        }
        this.updateHandleValue();
        this.cd.markForCheck();
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cd.markForCheck();
    }

    get rangeStartLeft() {
        return this.isVertical() ? 'auto' : this.handleValues[0] + '%';
    }

    get rangeStartBottom() {
        return this.isVertical() ? this.handleValues[0] + '%' : 'auto';
    }

    get rangeEndLeft() {
        return this.isVertical() ? 'auto' : this.handleValues[1] + '%';
    }

    get rangeEndBottom() {
        return this.isVertical() ? this.handleValues[1] + '%' : 'auto';
    }

    isVertical(): boolean {
        return this.orientation === 'vertical';
    }

    updateDomData(): void {
        const rect = this.el.nativeElement.children[0].getBoundingClientRect();
        this.initX = rect.left + DomHandler.getWindowScrollLeft();
        this.initY = rect.top + DomHandler.getWindowScrollTop();
        this.barWidth = this.el.nativeElement.children[0].offsetWidth;
        this.barHeight = this.el.nativeElement.children[0].offsetHeight;
    }

    calculateHandleValue(event): number {
        if (this.orientation === 'horizontal') {
            return ((event.pageX - this.initX) * 100) / (this.barWidth);
        } else {
            return(((this.initY + this.barHeight) - event.pageY) * 100) / (this.barHeight);
        }
    }

    updateHandleValue(): void {
        if (this.range) {
            this.handleValues[0] = (this.values[0] < this.min ? 0 : this.values[0] - this.min) * 100 / (this.max - this.min);
            this.handleValues[1] = (this.values[1] > this.max ? 100 : this.values[1] - this.min) * 100 / (this.max - this.min);
        } else {
            if (this.value < this.min) {
                this.handleValue = 0;
            } else if (this.value > this.max) {
                this.handleValue = 100;
            } else {
                this.handleValue = (this.value - this.min) * 100 / (this.max - this.min);
            }
        }
    }

    updateValue(val: number, event?: Event, isMouseEvent: boolean = false): void {
        if (this.range) {
            let value = val;

            if (this.handleIndex === 0) {
                if (value < this.min) {
                    value = this.min;
                    this.handleValues[0] = 0;
                } else if (value > this.values[1]) {
                    value = this.values[1];
                    this.handleValues[0] = this.handleValues[1];
                }

                this.sliderHandleStart.nativeElement.focus();
            } else {
                if (value > this.max) {
                    value = this.max;
                    this.handleValues[1] = 100;
                } else if (value < this.values[0]) {
                    value = this.values[0];
                    this.handleValues[1] = this.handleValues[0];
                }

                this.sliderHandleEnd.nativeElement.focus();
            }

            this.values[this.handleIndex] = this.getNormalizedValue(value);
            this.values = this.values.slice();
            if (!isMouseEvent) {
                this.updateParentValues(event);
            }
        } else {
            if (val < this.min) {
                val = this.min;
                this.handleValue = 0;
            } else if (val > this.max) {
                val = this.max;
                this.handleValue = 100;
            }

            this.value = this.getNormalizedValue(val);
            if (!isMouseEvent) {
                this.updateParentValue(event);
            }
            this.sliderHandle.nativeElement.focus();
        }
    }

    // Waiting 300ms to update everything
    @Debounce(300, {leading: false})
    // For this.values
    updateParentValues(event?: Event) {
        this.onModelChange(this.values);
        this.onChange.emit({event: event, values: this.values});
    }

    @Debounce(300, {leading: false})
    // For this.value
    updateParentValue(event?: Event) {
        this.onModelChange(this.value);
        this.onChange.emit({event: event, value: this.value});
    }

    getValueFromHandle(handleValue: number): number {
        return (this.max - this.min) * (handleValue / 100) + this.min;
    }

    getDecimalsCount(value: number): number {
        if (value && Math.floor(value) !== value) {
            return value.toString().split('.')[1].length || 0;
        }
        return 0;
    }

    getNormalizedValue(val: number): number {
        const decimalsCount = this.getDecimalsCount(this.step);
        if (decimalsCount > 0) {
            return +val.toFixed(decimalsCount);
        } else {
            return Math.floor(val);
        }
    }

    ngOnDestroy() {
        this.unbindDragListeners();
    }
}
